import { AuthBaseClient } from '../base-client';
import type { Audience, CampaignSpec } from '@vagusx/shared';
import type {
  AudienceCatalogEntry,
  AudiencePreview,
  CampaignVersion,
  CompileCampaignInput,
  CompileResult,
  ContactPolicy,
  CreateCampaignInput,
  OutreachCampaign,
  PublishCampaignInput,
  SimulateCampaignInput,
  SimulationResult,
  UpdateCampaignInput,
  UpdateContactPolicyInput,
  CreatePatientOptoutInput,
  PatientOptout,
  CampaignExclusion,
  CampaignResults,
  CampaignResultsQuery,
  CreateExclusionInput,
  LaunchReadiness,
  QueuePage,
  QueueQuery,
  QueueRow,
  QueueSummary,
  WorkflowOption,
} from './types';
import {
  campaignResultsSchema,
  exclusionResponseSchema,
  launchReadinessSchema,
  patientOptoutListSchema,
  patientOptoutResponseSchema,
  queuePageSchema,
  queueRowSchema,
  queueSummarySchema,
  workflowOptionSchema,
} from './types';

/**
 * Client for the campaigns API, `/api/campaigns` (ADR-154; renamed from
 * `/api/outreach` by ADR-318).
 *
 * Two of these methods are what make a campaign judgeable before it calls
 * anyone: {@link previewAudience} runs the same query the reconciler runs, and
 * {@link simulate} runs the same decision function the driver runs. Neither is
 * an estimate.
 */
export class OutreachClient extends AuthBaseClient {
  // ───────────────────────────── catalog ─────────────────────────────

  /**
   * The audience catalog the condition picker renders. Served rather than
   * hardcoded, so adding an audience never means editing a list in the web app.
   */
  async audienceCatalog(): Promise<AudienceCatalogEntry[]> {
    return this.get<AudienceCatalogEntry[]>('/api/campaigns/audience-catalog');
  }

  /**
   * How many patients this audience matches right now.
   *
   * A POST because the audience is a document, not a query string.
   */
  async previewAudience(
    audience: Audience,
    placesCalls: boolean,
    signal?: AbortSignal
  ): Promise<AudiencePreview> {
    return this.post<AudiencePreview>(
      '/api/campaigns/preview-audience',
      { audience, placesCalls },
      signal
    );
  }

  /** What the campaign would do to one patient, before it does it to anyone. */
  async simulate(input: SimulateCampaignInput, signal?: AbortSignal): Promise<SimulationResult> {
    return this.post<SimulationResult>('/api/campaigns/simulate', input, signal);
  }

  /**
   * Rebuild a campaign from an edited description.
   *
   * Returns a working spec plus everything that moved — it publishes nothing.
   * Sends the current spec as well as the text, which is what keeps settings no
   * sentence mentions from being dropped.
   */
  async compile(input: CompileCampaignInput, signal?: AbortSignal): Promise<CompileResult> {
    return this.post<CompileResult>('/api/campaigns/compile', input, signal);
  }

  // ───────────────────────────── campaigns ─────────────────────────────

  async list(): Promise<OutreachCampaign[]> {
    return this.get<OutreachCampaign[]>('/api/campaigns');
  }

  async getById(id: string): Promise<OutreachCampaign> {
    return this.get<OutreachCampaign>(`/api/campaigns/${id}`);
  }

  /** Always created as a draft — a campaign with no published version has no spec. */
  async create(data: CreateCampaignInput): Promise<OutreachCampaign> {
    return this.post<OutreachCampaign>('/api/campaigns', data);
  }

  async update(id: string, data: UpdateCampaignInput): Promise<OutreachCampaign> {
    return this.patch<OutreachCampaign>(`/api/campaigns/${id}`, data);
  }

  /**
   * Publish a spec as an immutable version and point the campaign at it.
   *
   * This is also the save: there is no draft-spec storage, so an edited spec
   * lives in the editor until it is published.
   */
  async publish(id: string, data: PublishCampaignInput): Promise<OutreachCampaign> {
    return this.post<OutreachCampaign>(`/api/campaigns/${id}/publish`, data);
  }

  /**
   * Start calling. Needs `outreach:run`, and something published (ADR-318).
   * Activating an active campaign is a no-op.
   */
  async activate(id: string): Promise<OutreachCampaign> {
    return this.post<OutreachCampaign>(`/api/campaigns/${id}/activate`, {});
  }

  /** Stop calling. Needs `outreach:run`; a draft cannot be paused. */
  async pause(id: string): Promise<OutreachCampaign> {
    return this.post<OutreachCampaign>(`/api/campaigns/${id}/pause`, {});
  }

  /** Version history, with how many runs are still pinned to each. */
  async listVersions(id: string): Promise<CampaignVersion[]> {
    return this.get<CampaignVersion[]>(`/api/campaigns/${id}/versions`);
  }

  // ───────────────────────────── contact policy ─────────────────────────────

  /** The org-wide policy. Created with defaults on first read. */
  async getContactPolicy(): Promise<ContactPolicy> {
    return this.get<ContactPolicy>('/api/campaigns/rules');
  }

  async updateContactPolicy(data: UpdateContactPolicyInput): Promise<ContactPolicy> {
    return this.put<ContactPolicy>('/api/campaigns/rules', data);
  }

  // ───────────────────────────── launch (ADR-318, F) ─────────────────────────────

  /** Published outbound voice workflows a step may run. */
  async workflowOptions(): Promise<WorkflowOption[]> {
    const data = await this.get<unknown>('/api/campaigns/workflow-options');
    return workflowOptionSchema.array().parse(data);
  }

  /** Can this spec run? Every check, pass or not; activate runs the same one. */
  async launchCheck(spec: CampaignSpec, signal?: AbortSignal): Promise<LaunchReadiness> {
    const data = await this.post<unknown>('/api/campaigns/launch-check', { spec }, signal);
    return launchReadinessSchema.parse(data);
  }

  // ───────────────────────────── queue ─────────────────────────────

  async queueSummary(id: string): Promise<QueueSummary> {
    const data = await this.get<unknown>(`/api/campaigns/${id}/queue/summary`);
    return queueSummarySchema.parse(data);
  }

  /** One lane's page. Pass back `nextCursor` for the next. */
  async queuePage(id: string, query: QueueQuery): Promise<QueuePage> {
    const params = new URLSearchParams({ lane: query.lane, limit: String(query.limit) });
    if (query.cursor) params.set('cursor', query.cursor);
    const data = await this.get<unknown>(`/api/campaigns/${id}/queue?${params.toString()}`);
    return queuePageSchema.parse(data);
  }

  /** Bring a waiting run's next try forward to now; the gates still decide. */
  async runNow(runId: string): Promise<QueueRow> {
    const data = await this.post<unknown>(`/api/campaigns/runs/${runId}/run-now`, {});
    return queueRowSchema.parse(data);
  }

  /** Take a patient out of this campaign, with or without a run. */
  async excludePatient(id: string, input: CreateExclusionInput): Promise<CampaignExclusion> {
    const data = await this.post<unknown>(`/api/campaigns/${id}/exclusions`, input);
    const { exclusion } = exclusionResponseSchema.parse(data);
    if (!exclusion) throw new Error('The exclusion was not returned');
    return exclusion;
  }

  /** Put a patient back; reconciliation decides whether a run starts again. */
  async includePatient(id: string, patientId: string): Promise<null> {
    const data = await this.delete<unknown>(`/api/campaigns/${id}/exclusions/${patientId}`);
    exclusionResponseSchema.parse(data);
    return null;
  }

  // ───────────────────────────── results ─────────────────────────────

  /** Without a range: the practice's last 30 days, in its own timezone (see `range` in the answer). */
  async results(id: string, query: CampaignResultsQuery = {}): Promise<CampaignResults> {
    const params =
      query.from && query.to ? `?${new URLSearchParams({ from: query.from, to: query.to })}` : '';
    const data = await this.get<unknown>(`/api/campaigns/${id}/results${params}`);
    return campaignResultsSchema.parse(data);
  }

  // ───────────────────────────── patient opt-outs ─────────────────────────────

  /** A patient's opt-outs, revoked ones included, newest first (ADR-318, E). */
  async listPatientOptouts(patientId: string): Promise<PatientOptout[]> {
    const data = await this.get<unknown>(`/api/patients/${patientId}/optouts`);
    return patientOptoutListSchema.parse(data).optouts;
  }

  /** Record an opt-out the patient asked a member of staff for. */
  async createPatientOptout(
    patientId: string,
    input: CreatePatientOptoutInput
  ): Promise<PatientOptout> {
    const data = await this.post<unknown>(`/api/patients/${patientId}/optouts`, input);
    return patientOptoutResponseSchema.parse(data).optout;
  }

  /** Revoke an opt-out. Needs a person: the API refuses automation. */
  async revokePatientOptout(patientId: string, optoutId: string): Promise<PatientOptout> {
    const data = await this.post<unknown>(
      `/api/patients/${patientId}/optouts/${optoutId}/revoke`,
      {}
    );
    return patientOptoutResponseSchema.parse(data).optout;
  }
}
